import React, { useEffect, useMemo, useState } from "react";
import { Loader2, Package, Trash2, X } from "lucide-react";
import { BusinessService } from "../../types/businessService";
import { Product } from "../../types/product";
import { ManageDeal } from "../../services/manageDealsApi";
import { cn } from "../../lib/utils";
import { stripHtml } from "../../lib/stripHtml";
import { useAuth } from "../../auth/AuthContext";

export type DealDraftItem = {
  key: string;
  type: "service" | "product";
  id: number;
  name: string;
  quantity: number;
  unit_price: number;
  discount_amount: number;
};

export type DealFormSavePayload = {
  title: string;
  description?: string;
  status: "active" | "inactive";
  items: Array<{
    type: "service" | "product";
    id: number;
    quantity: number;
    unit_price: number;
    discount_amount: number;
  }>;
};

interface DealFormProps {
  mode: "create" | "edit";
  deal?: ManageDeal | null;
  services: BusinessService[];
  products: Product[];
  catalogLoading?: boolean;
  formLoading?: boolean;
  saving?: boolean;
  onSave: (payload: DealFormSavePayload) => void | Promise<void>;
  onCancel: () => void;
}

const money = (n: number, symbol: string) =>
  `${symbol}${Number(n || 0).toFixed(2)}`;

export const DealForm: React.FC<DealFormProps> = ({
  mode,
  deal,
  services,
  products,
  catalogLoading = false,
  formLoading = false,
  saving = false,
  onSave,
  onCancel,
}) => {
  const { user } = useAuth();
  const currency =
    user?.company?.currency_symbol ||
    user?.company?.currency?.currency_symbol ||
    "";

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [status, setStatus] = useState<"active" | "inactive">("active");
  const [items, setItems] = useState<DealDraftItem[]>([]);
  const [pickerType, setPickerType] = useState<"service" | "product">("service");
  const [pickerSearch, setPickerSearch] = useState("");

  useEffect(() => {
    if (mode === "edit" && deal) {
      setTitle(deal.title || "");
      setDescription(stripHtml(deal.description || ""));
      setStatus((deal.status as "active" | "inactive") || "active");
      setItems(
        (deal.items || []).map((it, idx) => ({
          key: `${it.type}-${it.item_id}-${idx}`,
          type: it.type,
          id: it.item_id,
          name: it.name || `${it.type} #${it.item_id}`,
          quantity: Number(it.quantity || 1),
          unit_price: Number(it.unit_price || 0),
          discount_amount: Number(it.discount_amount || 0),
        })),
      );
    } else {
      setTitle("");
      setDescription("");
      setStatus("active");
      setItems([]);
    }
    setPickerSearch("");
    setPickerType("service");
  }, [mode, deal]);

  const totals = useMemo(() => {
    let original = 0;
    let dealTotal = 0;
    items.forEach((it) => {
      const gross = it.quantity * it.unit_price;
      original += gross;
      dealTotal += Math.max(0, gross - it.discount_amount);
    });
    return {
      original: Math.round(original * 100) / 100,
      deal: Math.round(dealTotal * 100) / 100,
    };
  }, [items]);

  const pickerOptions = useMemo(() => {
    const q = pickerSearch.trim().toLowerCase();
    if (pickerType === "service") {
      return services
        .filter((s) => !q || s.name.toLowerCase().includes(q))
        .slice(0, 40)
        .map((s) => ({
          id: Number(s.id),
          name: s.name,
          price: Number(s.discountPrice ?? s.price ?? 0),
        }));
    }
    return products
      .filter((p) => !q || p.name.toLowerCase().includes(q))
      .slice(0, 40)
      .map((p) => ({
        id: Number(p.id),
        name: p.name,
        price: Number(p.discountedPrice ?? p.price ?? 0),
      }));
  }, [pickerType, pickerSearch, services, products]);

  const addItem = (opt: { id: number; name: string; price: number }) => {
    const exists = items.find((i) => i.type === pickerType && i.id === opt.id);
    if (exists) {
      setItems((prev) =>
        prev.map((i) =>
          i.key === exists.key ? { ...i, quantity: i.quantity + 1 } : i,
        ),
      );
      return;
    }
    setItems((prev) => [
      ...prev,
      {
        key: `${pickerType}-${opt.id}-${Date.now()}`,
        type: pickerType,
        id: opt.id,
        name: opt.name,
        quantity: 1,
        unit_price: opt.price,
        discount_amount: 0,
      },
    ]);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    void onSave({
      title: title.trim(),
      description: description.trim() || undefined,
      status,
      items: items.map((it) => ({
        type: it.type,
        id: it.id,
        quantity: it.quantity,
        unit_price: it.unit_price,
        discount_amount: it.discount_amount,
      })),
    });
  };

  const busy = saving || formLoading;

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-[2px] flex items-center justify-center p-0 sm:p-4 z-[1000]">
      <div className="bg-white sm:rounded-xl shadow-2xl w-full max-w-4xl flex flex-col max-h-[90vh] relative">
        <div className="flex items-center justify-between px-6 pt-6 pb-4 border-b border-gray-100 shrink-0">
          <h2 className="text-xl font-bold text-[#402B69]">
            {mode === "edit" ? "Edit Deal" : "Add New Deal"}
          </h2>
          <button
            type="button"
            onClick={onCancel}
            disabled={saving}
            className="p-2 text-gray-400 hover:text-[#885ABB] hover:bg-purple-50 rounded-lg transition-all disabled:opacity-50"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex-1 flex flex-col min-h-0">
          <div className="flex-1 overflow-y-auto p-4 sm:p-6 space-y-5 custom-scrollbar">
            {formLoading ? (
              <div className="flex justify-center py-16">
                <Loader2 className="h-8 w-8 animate-spin text-[#885ABB]" />
              </div>
            ) : (
              <>
                <section className="border border-gray-200 rounded-lg">
                  <div className="px-6 py-3 border-b border-gray-200 bg-gray-50">
                    <h3 className="text-lg font-semibold text-gray-900">
                      Deal Details
                    </h3>
                  </div>
                  <div className="p-6 space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-900 mb-1">
                        Title *
                      </label>
                      <input
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        className="w-full h-11 rounded-lg border border-gray-300 px-3 text-gray-900 focus:outline-none focus:ring-2 focus:ring-purple-600"
                        placeholder="e.g. Cut & Colour Package"
                        disabled={busy}
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-900 mb-1">
                        Description
                      </label>
                      <textarea
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        rows={4}
                        className="w-full rounded-lg border border-gray-300 px-3 py-2 text-gray-900 focus:outline-none focus:ring-2 focus:ring-purple-600"
                        placeholder="Short description of this deal..."
                        disabled={busy}
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-900 mb-1">
                        Status
                      </label>
                      <select
                        value={status}
                        onChange={(e) =>
                          setStatus(e.target.value as "active" | "inactive")
                        }
                        className="w-full h-11 rounded-lg border border-gray-300 px-3 text-gray-900 focus:outline-none focus:ring-2 focus:ring-purple-600"
                        disabled={busy}
                      >
                        <option value="active">Active</option>
                        <option value="inactive">Inactive</option>
                      </select>
                    </div>
                  </div>
                </section>

                <section className="border border-gray-200 rounded-lg">
                  <div className="px-6 py-3 border-b border-gray-200 bg-gray-50 flex items-center justify-between gap-3">
                    <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                      <Package size={18} /> Services & Products
                    </h3>
                    <div className="flex rounded-lg border border-gray-200 p-0.5 bg-white">
                      <button
                        type="button"
                        onClick={() => setPickerType("service")}
                        className={cn(
                          "px-3 py-1 text-xs font-bold rounded-md",
                          pickerType === "service"
                            ? "bg-[#885ABB] text-white"
                            : "text-gray-600",
                        )}
                      >
                        Services
                      </button>
                      <button
                        type="button"
                        onClick={() => setPickerType("product")}
                        className={cn(
                          "px-3 py-1 text-xs font-bold rounded-md",
                          pickerType === "product"
                            ? "bg-[#885ABB] text-white"
                            : "text-gray-600",
                        )}
                      >
                        Products
                      </button>
                    </div>
                  </div>

                  <div className="p-6 space-y-4">
                    <input
                      value={pickerSearch}
                      onChange={(e) => setPickerSearch(e.target.value)}
                      placeholder={`Search ${pickerType}s...`}
                      className="w-full h-11 rounded-lg border border-gray-300 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-purple-600"
                      disabled={busy}
                    />

                    <div className="max-h-40 overflow-y-auto rounded-lg border border-gray-100 divide-y">
                      {catalogLoading ? (
                        <p className="p-3 text-sm text-gray-500">Loading...</p>
                      ) : pickerOptions.length === 0 ? (
                        <p className="p-3 text-sm text-gray-500">No results</p>
                      ) : (
                        pickerOptions.map((opt) => (
                          <button
                            key={`${pickerType}-${opt.id}`}
                            type="button"
                            onClick={() => addItem(opt)}
                            disabled={busy}
                            className="w-full flex items-center justify-between px-3 py-2 text-left hover:bg-purple-50 disabled:opacity-50"
                          >
                            <span className="text-sm font-medium text-[#402B69]">
                              {opt.name}
                            </span>
                            <span className="text-xs font-semibold text-[#885ABB]">
                              {money(opt.price, currency)} · Add
                            </span>
                          </button>
                        ))
                      )}
                    </div>

                    <div className="space-y-2">
                      {items.length === 0 ? (
                        <p className="text-sm text-gray-500">
                          No items in this deal yet.
                        </p>
                      ) : (
                        items.map((it) => (
                          <div
                            key={it.key}
                            className="rounded-xl border border-gray-100 p-3 space-y-2"
                          >
                            <div className="flex items-start justify-between gap-2">
                              <div>
                                <p className="text-sm font-semibold text-[#402B69]">
                                  {it.name}
                                </p>
                                <p className="text-[11px] uppercase text-gray-400 font-bold">
                                  {it.type}
                                </p>
                              </div>
                              <button
                                type="button"
                                onClick={() =>
                                  setItems((prev) =>
                                    prev.filter((x) => x.key !== it.key),
                                  )
                                }
                                disabled={busy}
                                className="text-red-500 hover:bg-red-50 rounded-lg p-1 disabled:opacity-50"
                              >
                                <Trash2 size={14} />
                              </button>
                            </div>
                            <div className="grid grid-cols-3 gap-2">
                              <div>
                                <label className="text-[10px] font-bold text-gray-400 uppercase">
                                  Qty
                                </label>
                                <input
                                  type="number"
                                  min={1}
                                  value={it.quantity}
                                  onChange={(e) =>
                                    setItems((prev) =>
                                      prev.map((x) =>
                                        x.key === it.key
                                          ? {
                                              ...x,
                                              quantity: Math.max(
                                                1,
                                                Number(e.target.value) || 1,
                                              ),
                                            }
                                          : x,
                                      ),
                                    )
                                  }
                                  className="w-full rounded-lg border border-gray-200 px-2 py-1.5 text-sm"
                                  disabled={busy}
                                />
                              </div>
                              <div>
                                <label className="text-[10px] font-bold text-gray-400 uppercase">
                                  Unit
                                </label>
                                <input
                                  type="number"
                                  min={0}
                                  step="0.01"
                                  value={it.unit_price}
                                  onChange={(e) =>
                                    setItems((prev) =>
                                      prev.map((x) =>
                                        x.key === it.key
                                          ? {
                                              ...x,
                                              unit_price: Math.max(
                                                0,
                                                Number(e.target.value) || 0,
                                              ),
                                            }
                                          : x,
                                      ),
                                    )
                                  }
                                  className="w-full rounded-lg border border-gray-200 px-2 py-1.5 text-sm"
                                  disabled={busy}
                                />
                              </div>
                              <div>
                                <label className="text-[10px] font-bold text-gray-400 uppercase">
                                  Discount
                                </label>
                                <input
                                  type="number"
                                  min={0}
                                  step="0.01"
                                  value={it.discount_amount}
                                  onChange={(e) =>
                                    setItems((prev) =>
                                      prev.map((x) =>
                                        x.key === it.key
                                          ? {
                                              ...x,
                                              discount_amount: Math.max(
                                                0,
                                                Number(e.target.value) || 0,
                                              ),
                                            }
                                          : x,
                                      ),
                                    )
                                  }
                                  className="w-full rounded-lg border border-gray-200 px-2 py-1.5 text-sm"
                                  disabled={busy}
                                />
                              </div>
                            </div>
                          </div>
                        ))
                      )}
                    </div>

                    <div className="rounded-xl bg-[#F8F5FC] p-3 space-y-1">
                      <div className="flex justify-between text-sm text-gray-600">
                        <span>Original</span>
                        <span>{money(totals.original, currency)}</span>
                      </div>
                      <div className="flex justify-between text-base font-bold text-[#885ABB]">
                        <span>Deal price</span>
                        <span>{money(totals.deal, currency)}</span>
                      </div>
                    </div>
                  </div>
                </section>
              </>
            )}
          </div>

          <div className="px-6 py-4 border-t border-gray-100 flex justify-end gap-2 shrink-0 bg-white sm:rounded-b-xl">
            <button
              type="button"
              onClick={onCancel}
              className="px-4 py-2 rounded-lg border border-gray-200 text-sm font-semibold text-gray-700 hover:bg-gray-50"
              disabled={saving}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={busy}
              className="px-5 py-2 rounded-lg bg-[#885ABB] text-white text-sm font-semibold hover:bg-[#7748A4] disabled:opacity-60 flex items-center gap-2"
            >
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Saving...
                </>
              ) : mode === "edit" ? (
                "Update Deal"
              ) : (
                "Create Deal"
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default DealForm;
